import { useFormContext } from "react-hook-form";
import { AddressLookupResultItemWithId, NetworkError } from "../common/models";
import { useCallback, useState } from "react";
import { GeocodedAddress } from "../api/models/mainservice";
import commonClient from "../api/private/commonClient";
import { handleApiErrorWithNotification } from "./errorHandling";
import { GroupBase, OptionsOrGroups } from "react-select";
import { SelectItem } from "../components/select/Select";
import { useGetCompanyCustomerInfoQuery } from "../common/queries";

export const oneLineAddress = (address: {
  street: string;
  postalCode: string;
  city: string;
}) => {
  return `${address.street}, ${address.postalCode} ${address.city}`;
};

export const inputNameWithPrefx = (name: string, prefix?: string) => {
  if (prefix) return `${prefix}.${name}`;
  return name;
};

export const useAddressFormOperations = () => {
  const { setValue } = useFormContext();

  // const [addressSelectedSuccessfully, setAddressSelectedSuccessfully] =
  //   useState(false);
  const [addressesResult, setAddressesResult] = useState<
    AddressLookupResultItemWithId[]
  >([]);

  const searchProvider =
    useGetCompanyCustomerInfoQuery().data?.company.addressSearchProvider ??
    "Google";

  const loadAddresses = useCallback(
    async (
      searchPhrase: string,
      callback: (
        options: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
      ) => void,
    ) => {
      if (!searchPhrase.trim()) return;

      try {
        const result = await commonClient.searchForAddresses(
          searchPhrase,
          searchProvider,
        );

        const addresses = result.results.map((x, i) => ({
          ...x,
          id: i.toString(),
        }));

        setAddressesResult(addresses);
        callback(
          addresses.map((x) => ({
            value: x.id,
            description: x.addressSecondLine,
            label: x.address,
          })),
        );
      } catch (e) {
        handleApiErrorWithNotification(e as NetworkError);
      }
    },
    [searchProvider],
  );

  const updateInputsValues = useCallback(
    (data: GeocodedAddress, prefix?: string) => {
      setValue(
        inputNameWithPrefx("street", prefix),
        { value: "test", label: data.street },
        {
          shouldValidate: true,
        },
      );
      setValue(inputNameWithPrefx("city", prefix), data.city, {
        shouldValidate: true,
      });
      setValue(inputNameWithPrefx("postalCode", prefix), data.postalCode, {
        shouldValidate: true,
      });
      setValue(inputNameWithPrefx("country", prefix), data.country, {
        shouldValidate: true,
      });
      setValue(
        inputNameWithPrefx("coordinates.latitude", prefix),
        data.latitude,
        {
          shouldValidate: true,
        },
      );
      setValue(
        inputNameWithPrefx("coordinates.longitude", prefix),
        data.longitude,
        {
          shouldValidate: true,
        },
      );
    },
    [setValue],
  );

  const cleanInputsValues = useCallback(
    (prefix?: string) => {
      setValue(inputNameWithPrefx("street", prefix), undefined);
      setValue(inputNameWithPrefx("city", prefix), undefined);
      setValue(inputNameWithPrefx("postalCode", prefix), undefined);
      setValue(inputNameWithPrefx("country", prefix), undefined);
      setValue(inputNameWithPrefx("coordinates.latitude", prefix), undefined);
      setValue(inputNameWithPrefx("coordinates.longitude", prefix), undefined);
    },
    [setValue],
  );

  const handleSelect = useCallback(
    async (
      addressId: string,
      prefix?: string,
      updateFakeStreet?: boolean,
      showAllFields?: () => void,
    ) => {
      const selectedAddress = addressesResult.find((x) => x.id === addressId);

      if (searchProvider === "Seznam" && selectedAddress?.details) {
        if (updateFakeStreet && !selectedAddress.details.isInacurateResult)
          setValue("fakeStreet", {
            value: addressId,
            label: oneLineAddress(selectedAddress.details),
          });

        updateInputsValues(selectedAddress.details, prefix);

        if (showAllFields && selectedAddress.details.isInacurateResult)
          showAllFields();
      }

      if (searchProvider === "Google" && selectedAddress?.placeId) {
        const endpoint = commonClient.geocodePlaceId;

        const result = await endpoint(selectedAddress.placeId, "Google");

        if (result.geocodedAddress) {
          if (updateFakeStreet && !result.geocodedAddress.isInacurateResult)
            setValue("fakeStreet", {
              value: addressId,
              label: oneLineAddress(result.geocodedAddress),
            });

          updateInputsValues(result.geocodedAddress, prefix);

          if (showAllFields && result.geocodedAddress.isInacurateResult)
            showAllFields();
        }
      }
    },
    [addressesResult, updateInputsValues, setValue, searchProvider],
  );

  return {
    loadAddresses,
    addressesResult,
    handleSelect,
    cleanInputsValues,
    updateInputsValues,
  };
};
